import _ from 'lodash';
import {
  sqCapsuleRuntimeStore,
  sqDurationStore,
  sqItemRuntimeStore,
  sqSeriesRuntimeStore,
  sqTrendConditionStore,
  sqTrendSeriesStore,
  sqTrendStore,
} from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { inflateTimes } from '@/datetime/dateTime.utilities';
import { cancelGroup } from '@/requests/pendingRequests.utilities';
import { takeCapsulesFromSelection } from '@/tools/manualCondition/capsuleGroup.utilities';
import {
  conditionFormula,
  makeCapsulesUnique,
  removeInvalidCapsules,
} from '@/tools/manualCondition/conditionFormula.service';
import {
  clearRuntimePreviewChartItem,
  recomputeRuntimeChartItems,
  removeRuntimeConditionFromPreviewLane,
  writeRuntimeEmptyPreviewLane,
  writeRuntimePreviewChartItem,
  writeRuntimeStitchDetails,
} from '@/trendData/capsuleRuntime.actions';
import { displayRange } from '@/trendData/duration.actions';
import { getCapsuleItems } from '@/trendData/trendCapsule.selectors';
import { deriveUncertainCapsuleReplacements } from '@/trendData/trendCapsule.utilities';
import { TREND_CAPSULE_INFLATION, TREND_PANELS, TREND_VIEWS } from '@/trendData/trendData.constants';
import { deriveUncertainCapsuleSeriesRenames } from '@/trendData/trendSeries.utilities';
import { computeCapsules } from '@/utilities/formula.utilities';
import type { PushOption } from '@/core/flux.service';
import type { CapsuleToAdd } from '@/trendData/trendData.types';

const previewCapsulesCancellationGroup = 'previewChartCapsules';

/**
 * Records fetched capsules. When a capsule replaces an uncertain one, the capsule store renames the
 * matching capsule series in place, so their runtime entries move to the new ids with them.
 */
export function addCapsulesToTrendStores(
  payload: { capsules: CapsuleToAdd[]; numPixels: number },
  ...option: [PushOption?]
): void {
  const uncertainReplacements = deriveUncertainCapsuleReplacements(getCapsuleItems(), payload.capsules);
  const swaps = Object.fromEntries(
    deriveUncertainCapsuleSeriesRenames(sqTrendSeriesStore.items, uncertainReplacements).map(
      ({ seriesId, newSeriesId }) => [seriesId, newSeriesId],
    ),
  );
  sqSeriesRuntimeStore.actions.swapValues(swaps);
  sqItemRuntimeStore.actions.swapItems(swaps);
  sqCapsuleRuntimeStore.actions.setCapsules(payload.capsules);
  flux.dispatch('TREND_ADD_CAPSULES', { ...payload, uncertainReplacements }, ...option);
  if (sqTrendStore.view === TREND_VIEWS.CHAIN) {
    writeRuntimeStitchDetails(payload.numPixels);
  }
  if (!_.isEmpty(sqTrendStore.enabledColumns(TREND_PANELS.CHART_CAPSULES))) {
    recomputeRuntimeChartItems();
  }
}

/**
 * Generates capsules for preview purposes.
 *
 * @param formula - the formula to use to generate the capsules
 * @param parameters - the parameters to use to resolve the formula.
 * @param conditionId - the id of the condition or undefined if creating a new search
 * @param color - the color to display the resulting capsules in formatted in hex code (e.g. #CCCCCC)
 * @param [usePost=false] - if true make the request with POST /formula/run instead of GET /formula/run
 *
 * @returns {Promise} that resolves once the results have been added to the chart
 */
export function generatePreviewCapsules(
  formula: string,
  parameters: any,
  conditionId: string,
  color: string,
  usePost = false,
) {
  const condition = sqTrendConditionStore.findItem(conditionId) ?? sqCapsuleRuntimeStore.getState().previewChartItem;

  return cancelGroup(previewCapsulesCancellationGroup)
    .then(() =>
      computeCapsules({
        formula,
        parameters,
        range: sqDurationStore.displayRange,
        cancellationGroup: previewCapsulesCancellationGroup,
        usePost,
      }),
    )
    .then((result) => {
      const previewPayload = {
        formula,
        parameters,
        capsules: result,
        id: conditionId,
        existingCondition: condition,
        color,
        lane: condition?.lane ?? sqTrendStore.getNextLane(),
      };
      flux.dispatch('TREND_SET_CHART_CAPSULES_PREVIEW', previewPayload);
      writeRuntimePreviewChartItem(previewPayload);
    })
    .catch(() => {
      displayEmptyPreviewCapsuleLane();
    });
}

/**
 * Cancels the preview capsules
 */
export function cancelPreviewCapsules() {
  return cancelGroup(previewCapsulesCancellationGroup);
}

/**
 * Generate preview condition from passed capsules
 *
 * @param conditionId - Id for the set of capsules.
 * @param capsules - List capsules to preview.
 * @param color - Color of capsule preview e.g #eb4034.
 */
export const generatePreviewConditionFromCapsules = (
  conditionId: string,
  capsules: {
    id?: string;
    startTime: number;
    endTime: number;
  }[],
  color: string,
) => {
  if (capsules.length === 0) {
    removeConditionFromPreviewLane(conditionId);
    const condition = sqTrendConditionStore.findItem(conditionId) ?? sqCapsuleRuntimeStore.getState().previewChartItem;
    if (_.isEmpty(condition)) {
      displayEmptyPreviewCapsuleLane(); // Note: the empty lane isn't shown until it succeeds once
    }

    return Promise.resolve();
  }

  return Promise.resolve(capsules)
    .then((capsules) =>
      _.map(capsules, ({ startTime, endTime }) => ({
        startTime,
        endTime,
        properties: [],
      })),
    )
    .then(removeInvalidCapsules)
    .then(makeCapsulesUnique)
    .then(conditionFormula)
    .then((formula) =>
      generatePreviewCapsules(
        formula,
        {},
        conditionId,
        color,
        true, // usePost - Formulas can become large enough to exceed node's max http header size
      ),
    );
};

/**
 * Removes the preview Capsules from the trend.
 */
export function removePreviewCapsules() {
  flux.dispatch('TREND_REMOVE_CHART_CAPSULES_PREVIEW');
  clearRuntimePreviewChartItem();
}

/**
 * Displays an empty preview lane. Opposed to removePreviewCapsules it does not reset
 * the previous stored capsules if available.
 */
export function displayEmptyPreviewCapsuleLane() {
  writeRuntimeEmptyPreviewLane();
}

/**
 * Removes a condition lane from preview. It is used in removing preview conditions in situations whereby there
 * are multiple preview lanes belonging to different conditions.
 */
export function removeConditionFromPreviewLane(conditionId: string) {
  flux.dispatch('TREND_REMOVE_CAPSULE_SET_FROM_PREVIEW_LANE', { conditionId });
  removeRuntimeConditionFromPreviewLane(conditionId);
}

/**
 * Zooms out to the selected capsules, or to include all capsules if none are selected.
 */
export function zoomOutToCapsules() {
  const allCapsules = getCapsuleItems();
  const selectedCapsules = allCapsules.filter((capsule) => capsule.selected);
  const capsules = selectedCapsules.length ? selectedCapsules : allCapsules;
  if (capsules.length) {
    const minTime = (_.chain(capsules).map('startTime') as any).min().value();
    const maxTime = (_.chain(capsules).map('endTime') as any).max().value();
    const inflation = inflateTimes(minTime, maxTime, TREND_CAPSULE_INFLATION);
    displayRange.updateTimes(inflation.start, inflation.end);
  }
}

export function replaceCapsuleSelection(capsules: unknown[]) {
  flux.dispatch('TREND_REPLACE_CAPSULE_SELECTION', { capsules });
  recomputeRuntimeChartItems();
  takeCapsulesFromSelection();
}

export function setCanCapsulesExpand(canCapsulesExpand: boolean) {
  flux.dispatch('TREND_SET_CAN_CAPSULES_EXPAND', { canCapsulesExpand });
  recomputeRuntimeChartItems();
}
